<script setup lang="ts">
    import { computed } from 'vue'

    /**
     * The consent a host call waits for (spec `host-tools.md`, "Conferma"):
     * same frame and buttons as `PkToolApprovalCard`, which answers the
     * backend's suspended calls instead. Shown only while the call waits: once
     * answered, the call's own step or line says how it ended.
     *
     * A submit lists what it changes (spec `page-tools-improvements.md`, M2)
     * as a table of each field, what it was and what it becomes, marked as
     * Word marks a revision; the fields it sends unchanged fold under it.
     * Details without `before` are plain lines: a line without a label stands
     * alone.
     */
    const props = defineProps<{
        question: string
        details?: { label: string; value: string; before?: string }[]
        unchanged?: { label: string; value: string }[]
    }>()

    const emit = defineEmits<{
        respond: [approved: boolean]
    }>()

    const isChangeList = computed(
        () =>
            props.details?.some((detail) => detail.before !== undefined) ??
            false,
    )
    // A form the agent filled from empty has nothing to strike: no column
    // of what was there, and the additions read without a header
    const hasBefore = computed(
        () => props.details?.some((detail) => !!detail.before) ?? false,
    )
</script>

<template>
    <div
        class="pk-host-call-confirm-card w-full bg-surface p-sm rounded-xl border border-surface-3">
        <p class="text-14 text-word-2 m-0 mb-xs">{{ question }}</p>
        <table
            v-if="details?.length && isChangeList"
            class="pk-host-call-confirm-card__changes text-14 mb-xs">
            <thead v-if="hasBefore">
                <tr>
                    <th scope="col">{{ $t('toolApproval.fieldColumn') }}</th>
                    <th scope="col">{{ $t('toolApproval.beforeColumn') }}</th>
                    <th scope="col">{{ $t('toolApproval.afterColumn') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(detail, index) in details" :key="index">
                    <th scope="row">{{ detail.label }}</th>
                    <td v-if="hasBefore">
                        <del
                            v-if="detail.before"
                            class="pk-host-call-confirm-card__removed"
                            >{{ detail.before }}</del
                        >
                    </td>
                    <td>
                        <ins
                            v-if="detail.value"
                            class="pk-host-call-confirm-card__added"
                            >{{ detail.value }}</ins
                        >
                    </td>
                </tr>
            </tbody>
        </table>
        <dl
            v-else-if="details?.length"
            class="pk-host-call-confirm-card__details text-14 m-0 mb-xs">
            <template v-for="(detail, index) in details" :key="index">
                <dt v-if="detail.label" class="text-word-3 truncate">
                    {{ detail.label }}
                </dt>
                <dd
                    class="text-word-1 m-0 truncate"
                    :class="{
                        'pk-host-call-confirm-card__alone': !detail.label,
                    }">
                    {{ detail.value }}
                </dd>
            </template>
        </dl>
        <p v-else-if="unchanged?.length" class="text-12 text-word-3 m-0 mb-xs">
            {{ $t('toolApproval.noChanges') }}
        </p>
        <VvAccordion
            v-if="unchanged?.length"
            :title="$t('toolApproval.unchangedFields', unchanged.length)"
            modifiers="compact"
            class="mb-xs">
            <dl class="pk-host-call-confirm-card__details text-12 m-0">
                <template v-for="(field, index) in unchanged" :key="index">
                    <dt class="text-word-3">{{ field.label }}</dt>
                    <dd class="text-word-2 m-0">{{ field.value }}</dd>
                </template>
            </dl>
        </VvAccordion>
        <VvButtonGroup>
            <VvButton
                modifiers="primary-small"
                icon="ri:check-line"
                :label="$t('action.proceed')"
                @click.stop="emit('respond', true)" />
            <VvButton
                modifiers="action-quiet-small"
                icon="ri:close-line"
                :label="$t('action.cancel')"
                @click.stop="emit('respond', false)" />
        </VvButtonGroup>
    </div>
</template>

<style lang="scss">
    .pk-host-call-confirm-card__details {
        display: grid;
        grid-template-columns: minmax(0, max-content) minmax(0, 1fr);
        gap: var(--spacing-2) var(--spacing-12);
    }

    .pk-host-call-confirm-card__alone {
        grid-column: 1 / -1;
    }

    // Each field on a row of its own, the name taking two fifths: long names
    // and values wrap instead of pushing the values out of the card
    .pk-host-call-confirm-card__changes {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;

        th,
        td {
            padding: var(--spacing-4) var(--spacing-8) var(--spacing-4) 0;
            text-align: start;
            vertical-align: top;
            overflow-wrap: anywhere;
        }

        th:first-child {
            width: 40%;
        }

        thead th {
            font-size: var(--text-12);
            font-weight: var(--font-normal);
            color: var(--color-word-3);
        }

        tbody th {
            font-weight: var(--font-normal);
            color: var(--color-word-2);
        }

        tr + tr,
        thead + tbody tr:first-child {
            border-top: var(--border) solid var(--color-surface-3);
        }
    }

    // Marked as Word marks a revision, as the file cards of the chat do
    // (`PkToolChatFile`): struck where the old value goes, underlined where
    // the new one comes. The base styles take the line off `del`.
    .pk-host-call-confirm-card__removed {
        color: var(--u-danger-text);
        text-decoration: line-through;
    }

    .pk-host-call-confirm-card__added {
        color: var(--u-success-text);
        font-weight: var(--font-medium);
        text-decoration: underline;
        text-underline-offset: 0.2em;
    }
</style>
